import processAccordingNotificationType from './processAccordingNotificationType';
import i18n from 'src/utils/translate';
import config from 'config/config';
import { BBCODE, MENTIONED_USERS_ID } from 'src/constants/regex';

const getPostLink = (notification, type = 'posts') => {
  const post = notification.post.superParent || notification.post;

  const postId = post.id;
  const title = post.urlEncodedTitle;
  const responseId = notification.post.superParent ? notification.post.id : undefined;

  const responses = responseId !== undefined ? `?responses=${responseId}` : '';
  if (notification.post.superParent && notification.post.superParent.postTypeId === 6 && title !== '') {
    return `polls/${postId}/${title}${responses}/result`;
  } else {
    return `${type}/${postId}/${title}${responses}`;
  }
};

function defaultExtractor(notification) {
  const postAttributes = notification.post ? {
    groupId: notification.post.groupId,
    link: getPostLink(notification),
  } : {};

  return {
    ...postAttributes,
    date: notification.date,
    userId: notification.userId,
    activityType: notification.type,
  };
}

const getExtractors = () => ({
  like: function like(notification) {
    return {
      actionVerb: i18n.t('liked your post'),
      title: notification.post.snippet,
    };
  },
  followUser: function followUser() {
    return {
      actionVerb: i18n.t('is now following you!'),
    };
  },
  followPost: function followPost(notification) {
    return {
      actionVerb: i18n.t('saved your post'),
      title: notification.post.title,
    };
  },
  mention: function mention(notification) {
    return {
      actionVerb: i18n.t('has mentioned you in'),
      title: notification.post.title ? `${notification.post.title}` : ' ' + i18n.t('their reply'),
      userId: notification.post.authorId,
    };
  },
  mentionReply: function mention(notification) {
    return {
      actionVerb: i18n.t('has mentioned you in'),
      title: notification.post.superParent.title ? `${notification.post.superParent.title}` : ' ' + i18n.t('their reply'),
      snippet: notification.post.snippet,
      userId: notification.post.authorId,
    };
  },
  reply: function reply(notification) {
    return {
      actionVerb: i18n.t('responded to'),
      snippet: notification.post.snippet,
      title: notification.post.superParent.title,
      userId: notification.post.authorId,
    };
  },
  blog: function blog(notification) {
    const post = notification.post;
    const snippet = post.snippet.length > config.text.brief.length
      ? `${post.snippet.slice(0, config.text.brief.length)}...`
      : post.snippet;

    return {
      actionVerb: i18n.t('wrote'),
      snippet,
      title: post.title,
      totalResponses: post.totalResponses,
    };
  },
  answer: function answer(notification) {
    const title = notification.post.title
      || (notification.post.superParent && notification.post.superParent.title);

    return {
      actionVerb: i18n.t('answered in'),
      snippet: notification.post.snippet,
      title,
    };
  },
  poll: function blog(notification) {
    return {
      actionVerb: i18n.t('set a poll'),
      title: notification.post.title,
      link: getPostLink(notification, 'polls'),
    };
  },
  likeReply: function likeReply(notification) {
    return {
      actionVerb: i18n.t('liked your reply'),
      title: notification.post.snippet,
    };
  },
  customNotification: function customNotification(notification) {
    return {
      userId: null,
      text: notification.text,
      link: notification.link,
    };
  },
});

const replaceMentionsAndStripBBCode = (text, mentionedUsers) => {
  if (!text) return '';

  return text.replace(MENTIONED_USERS_ID, (_, userId) => {
    const user = mentionedUsers.find(u => u.userId == userId); // using '==' because we're comparing an int and a string
    return user ? user.username : '';
  }).replace(BBCODE, '');
};

export default function enforceNotificationStructure(notificationData, users = [], groups = []) {
  const extractors = getExtractors();
  const notification = {
    ...notificationData,
    post: {
      ...notificationData.post,
      snippet: notificationData.post && replaceMentionsAndStripBBCode(notificationData.post.snippet, users),
    },
  };
  const extract = processAccordingNotificationType(notification, extractors) || extractors.blog;
  const data = Object.assign(defaultExtractor(notification), extract(notification));

  const user = data.userId ? users.find(x => x.userId === data.userId) : null;
  const group = data.groupId ? groups.find(x => x.groupId === data.groupId) : null;
  const link = data.link && group
    ? `/${group.subDomain}/${data.link}`
    : user ? `/user/${user.username.toLowerCase()}` : data.link;

  const model = {
    ...data,
    link,
    user,
  };

  return model;
}
